import { getAccessToken } from 'utils/auth';
import { PLAYER_API_URL } from 'utils/urls';

import {
  BuyFreezesRequest,
  SeatFrameEntry,
  StreakFramesResponse,
  StreakSnapshot,
  StreakSnapshotResponse
} from 'components/Streaks/model/types';
import { mockStreakRequest, streakMockEnabled } from 'components/Streaks/state/streakMock';

/**
 * player-api streak routes (EN-11151 architecture doc; contract in
 * model/types.ts). In a lower environment `localStorage.hijack_streaks_mock`
 * serves fixtures instead (see streakMock.ts).
 */

/**
 * The request never got an answer: the device is offline, or fetch itself
 * failed (a TypeError: Safari's "Load failed", Chrome's "Failed to fetch").
 * `sent` is false only when the device was offline and nothing went out, so
 * the caller can say for sure that nothing changed. A server error response is
 * a plain Error carrying the server's message instead.
 */
export class StreakNetworkError extends Error {
  readonly sent: boolean;

  constructor(sent: boolean) {
    super(sent ? 'Could not reach the server' : 'No internet connection');
    this.name = 'StreakNetworkError';
    this.sent = sent;
  }
}

export const isStreakNetworkError = (e: unknown): e is StreakNetworkError =>
  e instanceof StreakNetworkError;

const deviceOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false;

async function requestBody(path: string, init: RequestInit): Promise<unknown> {
  const token = getAccessToken();
  if (!token) throw new Error('Not signed in');
  if (deviceOffline()) throw new StreakNetworkError(false);
  let response: Response;
  try {
    response = await fetch(`${PLAYER_API_URL}${path}`, {
      ...init,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      cache: 'no-store'
    });
  } catch (e) {
    // An abort stays an abort; only fetch's own TypeError is a network failure.
    if (e instanceof TypeError) throw new StreakNetworkError(true);
    throw e;
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const message = body && typeof body.error === 'string' ? body.error : 'Streak request failed';
    throw new Error(message);
  }
  return body;
}

async function request(path: string, init: RequestInit = {}): Promise<StreakSnapshot> {
  if (streakMockEnabled()) return mockStreakRequest(path, init);
  const body = await requestBody(path, init);
  const snapshot = (body as StreakSnapshotResponse | null)?.snapshot;
  if (!snapshot || typeof snapshot.version !== 'number') throw new Error('Invalid streak response');
  return snapshot;
}

export function getStreakStatus(signal?: AbortSignal): Promise<StreakSnapshot> {
  return request('/streaks/status', { method: 'GET', signal });
}

/** One balance pays for the whole purchase; the idempotency key makes a retry or double tap charge once. */
export function buyFreezes(body: BuyFreezesRequest): Promise<StreakSnapshot> {
  return request('/streaks/freezes', { method: 'POST', body: JSON.stringify(body) });
}

export function setStreakFrameVisible(showFrame: boolean): Promise<StreakSnapshot> {
  return request('/streaks/frame', { method: 'PUT', body: JSON.stringify({ showFrame }) });
}

/** Seat frames for up to 10 players at a table; every GUID asked for comes back. */
export async function getStreakFrames(guids: string[]): Promise<Record<string, SeatFrameEntry>> {
  if (streakMockEnabled()) return {};
  const query = encodeURIComponent(guids.join(','));
  const body = (await requestBody(`/streaks/frames?guids=${query}`, {
    method: 'GET'
  })) as StreakFramesResponse | null;
  if (!body || typeof body.frames !== 'object' || body.frames === null)
    throw new Error('Invalid streak response');
  return body.frames;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
